import React, { useState, useCallback, useEffect, useContext } from 'react';
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  Panel,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './styles.css';

import AccountContext from '../../../../contexts/AccountContext';
import TreeSearch from './components/TreeSearch';
import ItemNode from './components/ItemNode';
import VendorNode from './components/VendorNode';
import LootNode from './components/LootNode';
import useTreeData from './hooks/useTreeData';
import useItemNameSearch from './hooks/useItemNameSearch';
import { getLayoutedElements } from './utils/layoutHelpers';
import RHLoadingMask from '../../../../components/common/RHLoadingMask';
import { useSearchParams } from '../../../../hooks/useSearchParams';

const nodeTypes = {
  item: ItemNode,
  vendor: VendorNode,
  loot: LootNode,
};

function LootTreeView() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // URL parameter management
  const { updateSearchParams, getParam } = useSearchParams();
  
  // Initialize state from URL parameters
  const [rootId, setRootId] = useState(() => getParam('rootId') || '');
  const [rootType, setRootType] = useState(() => getParam('rootType') || 'item');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [isNewSearch, setIsNewSearch] = useState(false);
  const [itemNameQuery, setItemNameQuery] = useState('');

  const { currentStateData } = useContext(AccountContext);
  const sandboxId = currentStateData?.sandbox?.sandboxId;

  const { data: treeData, isLoading, error } = useTreeData(sandboxId, rootId, rootType, expandedNodes, isNewSearch);
  const { data: itemNameResults, isLoading: isItemNameLoading, error: itemNameError } = useItemNameSearch(sandboxId, itemNameQuery);
  
  // Handle node click for expand/collapse
  const onNodeClick = useCallback((event, node) => {
    if (node.data.hasChildren) {
      setExpandedNodes(prev => {
        const newSet = new Set(prev);
        if (newSet.has(node.id)) {
          newSet.delete(node.id);
        } else {
          newSet.add(node.id);
        }
        return newSet;
      });
    }
  }, []);
  
  // Layout calculation when data changes
  useEffect(() => {
    if (treeData) {
      const { nodes: layoutedNodes, edges: layoutedEdges } = 
        getLayoutedElements(treeData.nodes, treeData.edges, 'LR');
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
      
      // If this was a new search with auto-expand, mark all loaded nodes as expanded
      if (isNewSearch) {
        const allNodeIds = new Set(layoutedNodes.map(node => node.id));
        setExpandedNodes(allNodeIds);
        setIsNewSearch(false); // Reset the flag
      }
    }
  }, [treeData, setNodes, setEdges, isNewSearch]);
  
  if (!sandboxId) {
    return <div style={{ padding: '20px', color: '#fff', background: '#1a1a1a', height: 'calc(100vh - 64px)' }}>Please select a sandbox to view the tree.</div>;
  }
  
  return (
    <div style={{ width: '100%', height: 'calc(100vh - 64px)', background: '#1a1a1a' }}>
      {(isLoading && rootId || isItemNameLoading) && <RHLoadingMask />}
      {(error || itemNameError) && (
        <div style={{ 
          padding: '20px', 
          color: '#ff5252',
          background: 'rgba(255, 82, 82, 0.1)',
          border: '1px solid #ff5252',
          borderRadius: '4px',
          margin: '20px',
          fontSize: '14px',
          lineHeight: '1.4'
        }}>
          <strong>Error:</strong> {error?.message || itemNameError?.message}
        </div>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        style={{ background: '#1a1a1a' }}
      >
        <Panel position="top-left" style={{ margin: '10px', maxHeight: '80vh', overflow: 'auto' }}>
          <TreeSearch
            initialId={rootId}
            initialType={rootType}
            onSearch={(id, type) => {
              setItemNameQuery(''); // Clear item name search
              setRootId(id);
              setRootType(type);
              setExpandedNodes(new Set());
              setIsNewSearch(true);
              updateSearchParams({ rootId: id, rootType: type });
            }}
            onItemNameSearch={(query) => {
              setRootId(''); // Clear tree view
              setRootType('item');
              setExpandedNodes(new Set());
              setItemNameQuery(query);
              updateSearchParams({ itemName: query });
            }}
          />
          {itemNameResults && itemNameResults.length > 0 && (
            <div style={{
              marginTop: '8px',
              background: '#2d2d2d',
              border: '1px solid #444',
              borderRadius: '4px',
              maxHeight: '60vh',
              overflow: 'auto',
            }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #444', color: '#aaa', fontSize: '12px' }}>
                {itemNameResults.length} loot entries found
              </div>
              {itemNameResults.map((loot) => (
                <div
                  key={loot.loot_id}
                  onClick={() => {
                    setItemNameQuery('');
                    setRootId(loot.loot_id);
                    setRootType('loot');
                    setExpandedNodes(new Set());
                    setIsNewSearch(true);
                    updateSearchParams({ rootId: loot.loot_id, rootType: 'loot' });
                  }}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #333',
                    color: '#ddd',
                    fontSize: '13px',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#3d3d3d'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ fontWeight: 'bold', color: '#FF9800' }}>
                    {loot.name || `Loot ${loot.loot_id}`}
                  </div>
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                    Item: <span style={{ color: '#2196F3' }}>{loot.item_name || loot.item_id || 'None'}</span>
                    {' | '}Vendor: <span style={{ color: '#4CAF50' }}>{loot.vendor_name || loot.vendor_id}</span>
                    {loot.active ? ' ✓' : ' ✗'}
                  </div>
                </div>
              ))}
            </div>
          )}
          {itemNameResults && itemNameResults.length === 0 && itemNameQuery && (
            <div style={{
              marginTop: '8px',
              padding: '12px',
              background: '#2d2d2d',
              border: '1px solid #444',
              borderRadius: '4px',
              color: '#888',
              fontSize: '13px',
              textAlign: 'center',
            }}>
              No loot found with item name matching "{itemNameQuery}"
            </div>
          )}
        </Panel>
        <Controls />
        <MiniMap 
          style={{
            background: '#2d2d2d',
            border: '1px solid #444',
            borderRadius: '4px',
          }}
          nodeColor={(node) => {
            switch(node.type) {
              case 'item': return '#2196F3';
              case 'vendor': return '#4CAF50';
              case 'loot': return '#FF9800';
              default: return '#666';
            }
          }}
        />
        {!isLoading && !error && rootId && (!nodes || nodes.length === 0) && (
          <Panel position="center">
            <div style={{ 
              color: '#888', 
              padding: '20px',
              textAlign: 'center',
              background: '#2d2d2d',
              borderRadius: '4px',
              border: '1px solid #444'
            }}>
              No data found for this search.
            </div>
          </Panel>
        )}
        <Background variant="dots" gap={12} size={1} color="#333" />
      </ReactFlow>
    </div>
  );
}

export default function RHLootTreeViewWrapper() {
  return (
    <ReactFlowProvider>
      <LootTreeView />
    </ReactFlowProvider>
  );
} 